import { graphql } from '../../../../gql';
import { useModalContext } from '../../../../modal/ModalContext';
import { useApiMutation } from '../../../../query/hooks';
import { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSnackbar } from '../../../../store/snackbar';
import { mutationResponseHandler } from '../../../../utils/mutationResponseHandler';
import { z } from 'zod';
import { useTypedForm } from '../../../../../../shared/fe/hooks/useForm';
import { DialogContent, MenuItem, OutlinedInput, Stack, Typography } from '@mui/material';
import { ControlledFormField } from '../../../../form/ControlledFormField';
import { MetricTemplateSelect } from '../../../metrics/MetricTemplateSelect';
import { LoadableSelect } from '../../../../form/LoadableSelect';
import { ModalActions } from '../../../../modal/components/ModalActions';
import { UnmappedSheets } from '../MapUnmappedSheetsModal';

const handleSingleSheetMapping = graphql(`
	mutation mapSingleSheet($input: SingleSheetMappingConfig!) {
		setSingleSheetMapping(input: $input) {
			ok
			errorMessage
		}
	}
`);

const schema = z.object({
	metricName: z.string(),
	metricTemplateId: z.string(),
	sheetTitle: z.string(),
});

type FormValues = z.infer<typeof schema>;

type Props = Readonly<{
	spreadsheetExternalId: string;
	isLoading: boolean;
	unmappedSheets: UnmappedSheets;
}>;

export const MapToNewMetric: FC<Props> = ({ spreadsheetExternalId, isLoading, unmappedSheets }) => {
	const intl = useIntl();
	const { closeModal } = useModalContext();
	const form = useTypedForm({ schema });
	const { openSnackbar } = useSnackbar();
	const { mutateAsync } = useApiMutation(handleSingleSheetMapping, {
		onSuccess: (result) => {
			mutationResponseHandler({
				result: result.setSingleSheetMapping.ok,
				toastMessages: {
					success: intl.formatMessage({ id: 'map-unmapped-sheets-modal.success' }),
					error: intl.formatMessage({ id: 'map-unmapped-sheets-modal.error' }),
				},
				openSnackbar,
				callback: closeModal,
			});
		},
	});
	const onSubmit = async (values: FormValues) => {
		await mutateAsync({ input: { spreadsheetId: spreadsheetExternalId, ...values } });
	};

	return (
		<>
			<DialogContent>
				<form noValidate onSubmit={form.handleSubmit(onSubmit)}>
					<Stack gap={1.5}>
						<ControlledFormField
							form={form}
							label={intl.formatMessage({ id: 'map-unmapped-sheets-modal.metric-name' })}
							name={'metricName'}
							render={(props) => <OutlinedInput {...props} />}
						/>
						<ControlledFormField
							form={form}
							label={intl.formatMessage({ id: 'map-unmapped-sheets-modal.metric-template' })}
							name={'metricTemplateId'}
							render={(props) => <MetricTemplateSelect {...props} />}
						/>
						<ControlledFormField
							form={form}
							label={intl.formatMessage({ id: 'map-unmapped-sheets-modal.google-sheet' })}
							name={'sheetTitle'}
							render={(props) => (
								<LoadableSelect
									{...props}
									loading={isLoading}
									renderValue={(value) => {
										return <Typography>{isLoading ? <FormattedMessage id={'common.loading'} /> : value}</Typography>;
									}}
								>
									{unmappedSheets.map((sheet) => (
										<MenuItem key={sheet?.sheetTitle ?? ''} value={sheet?.sheetTitle ?? ''}>
											{sheet?.sheetTitle}
										</MenuItem>
									))}
								</LoadableSelect>
							)}
						/>
					</Stack>
				</form>
			</DialogContent>
			<ModalActions
				cancelButtonText={intl.formatMessage({ id: 'common.cancel' })}
				handleCancelButtonClick={closeModal}
				primaryButtonText={intl.formatMessage({ id: 'common.create' })}
				handlePrimaryButtonClick={form.handleSubmit(onSubmit)}
				disablePrimaryButton={form.formState.isSubmitting}
			/>
		</>
	);
};
